import styled from "@emotion/styled";

export const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 0;
  background: transparent;
  width: 100%;
`;

export const Section = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 28px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.02),
    0 2px 4px -1px rgba(0, 0, 0, 0.01);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.04),
      0 4px 6px -2px rgba(0, 0, 0, 0.01);
    border-color: rgba(226, 232, 240, 1);
  }
`;

export const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  font-family: inherit;

  svg {
    color: #64748b;
    width: 18px;
    height: 18px;
    stroke-width: 2.5;
  }
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const MetricIcon = styled.div<{ bgColor: string }>`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: ${(props) => `${props.bgColor}10`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.bgColor};
  transition: transform 0.3s ease;
`;

export const MetricCard = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 18px;
  padding: 24px;
  border: 1px solid #f1f5f9;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.05);
    border-color: #e2e8f0;

    .metric-icon {
      transform: scale(1.1);
    }
  }
`;

export const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const MetricTrend = styled.div<{ positive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => (props.positive ? "#10b981" : "#f43f5e")};
  background: ${(props) => (props.positive ? "#ecfdf5" : "#fff1f2")};
  padding: 4px 8px;
  border-radius: 8px;
`;

export const MetricValue = styled.div`
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 6px;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

export const MetricLabel = styled.div`
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

export const StatItem = styled.div`
  padding: 20px;
  background: #f8fafc;
  border-radius: 14px;
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &:hover {
    background: #ffffff;
    border-color: #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  }
`;

export const StatLabel = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const StatValue = styled.div<{ color?: string; size?: string }>`
  font-size: ${(props) => props.size || "24px"};
  font-weight: 800;
  color: ${(props) => props.color || "#1e293b"};
  letter-spacing: -0.01em;
`;

export const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 14px;
  border: 1px solid #f1f5f9;
  background: #ffffff;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

export const TableHeader = styled.thead`
  background: #f8fafc;
`;

export const TableRow = styled.tr`
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f940;
  }

  &:not(:last-child) {
    td {
      border-bottom: 1px solid #f1f5f9;
    }
  }
`;

export const TableHead = styled.th`
  padding: 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #f1f5f9;
`;

export const TableBody = styled.tbody``;

export const TableCell = styled.td`
  padding: 16px;
  font-size: 13px;
  color: #475569;
  font-weight: 600;
`;

export const StatusBadge = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  border: 1px solid ${(props) => `${props.color}30`};
`;

export const AmountText = styled.span<{ positive: boolean }>`
  font-weight: 700;
  color: ${(props) => (props.positive ? "#10b981" : "#f43f5e")};
`;

export const DealsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const DealCard = styled.div`
  padding: 20px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:hover {
    box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.05);
    border-color: #e2e8f0;
    transform: translateX(4px);
  }
`;

export const DealHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const DealTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.01em;
`;

export const DealBadge = styled.div`
  padding: 6px 14px;
  background: #f1f5f9;
  color: #64748b;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 800;
`;

export const DealInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const DealDetail = styled.div`
  font-size: 12px;
  color: #94a3b8;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
    color: #cbd5e1;
  }
`;

export const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 480px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }
`;

export const ReviewCard = styled.div`
  padding: 20px;
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;

  &:hover {
    background: #ffffff;
    border-color: #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  }
`;

export const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const ReviewCustomer = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
`;

export const ReviewRating = styled.div`
  display: flex;
  gap: 2px;
`;

export const ReviewComment = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 12px;
  font-weight: 500;
`;

export const ReviewProduct = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const PaymentDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const PaymentDetailItem = styled.div`
  padding: 18px;
  background: #f8fafc;
  border-radius: 14px;
  border: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:hover {
    background: #ffffff;
    border-color: #e2e8f0;
  }
`;

export const PaymentLabel = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const PaymentValue = styled.div`
  font-size: 15px;
  color: #1e293b;
  font-weight: 700;
`;

export const NotificationForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FormLabel = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const FormInput = styled.input`
  padding: 14px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s ease;
  font-weight: 600;

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: #94a3b8;
    box-shadow: 0 0 0 4px rgba(148, 163, 184, 0.08);
  }

  &::placeholder {
    color: #cbd5e1;
    font-weight: 500;
  }
`;

export const FormTextarea = styled.textarea`
  padding: 14px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  font-weight: 600;
  line-height: 1.6;

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: #94a3b8;
    box-shadow: 0 0 0 4px rgba(148, 163, 184, 0.08);
  }

  &::placeholder {
    color: #cbd5e1;
    font-weight: 500;
  }
`;

export const FormSelect = styled.select`
  appearance: none;
  padding: 14px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: #94a3b8;
    box-shadow: 0 0 0 4px rgba(148, 163, 184, 0.08);
  }
`;

export const SendButton = styled.button`
  padding: 16px 28px;
  background: #1e293b;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #0f172a;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 2.5;
  }
`;

export const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 10px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const BreakdownCard = styled.div<{ highlight?: boolean }>`
  padding: 24px;
  background: ${(props) =>
    props.highlight
      ? "linear-gradient(135deg, #1e293b 0%, #334155 100%)"
      : "#ffffff"};
  color: ${(props) => (props.highlight ? "#ffffff" : "inherit")};
  border-radius: 20px;
  border: 1px solid ${(props) => (props.highlight ? "transparent" : "#f1f5f9")};
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  ${(props) =>
    props.highlight &&
    `
    box-shadow: 0 10px 20px -5px rgba(30, 41, 59, 0.2);
  `}

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${(props) =>
      props.highlight
        ? "0 15px 30px -10px rgba(30, 41, 59, 0.3)"
        : "0 10px 15px -3px rgba(0, 0, 0, 0.04)"};
    border-color: ${(props) => (props.highlight ? "transparent" : "#e2e8f0")};
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${(props) => (props.highlight ? "#3b82f6" : "transparent")};
  }
`;

export const IconContainer = styled.div<{ color: string; highlight?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: ${(props) =>
    props.highlight ? "rgba(255,255,255,0.1)" : `${props.color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.highlight ? "#ffffff" : props.color)};
  margin-bottom: 4px;
`;

export const BreakdownLabel = styled.div<{ inverted?: boolean }>`
  font-size: 11px;
  font-weight: 800;
  color: ${(props) => (props.inverted ? "rgba(255,255,255,0.7)" : "#94a3b8")};
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

export const BreakdownValue = styled.div<{
  color?: string;
  inverted?: boolean;
}>`
  font-size: 24px;
  font-weight: 800;
  color: ${(props) => (props.inverted ? "#ffffff" : props.color || "#0f172a")};
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

export const BreakdownSubtext = styled.div<{ inverted?: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.inverted ? "rgba(255,255,255,0.6)" : "#64748b")};
  font-weight: 600;
  margin-top: -2px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const BreakdownFooter = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px dashed #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
`;

export const TotalBadge = styled.div`
  padding: 10px 20px;
  background: #f8fafc;
  color: #1e293b;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 10px;

  span {
    color: #64748b;
    font-weight: 600;
    font-size: 12px;
  }
`;
